import {
  isLayoutViewportShrunkForKeyboard,
  isNativeKeyboardOpen,
  measureNativeKeyboardOpen,
  measureViewportKeyboardOcclusionPx,
} from "@/lib/capacitor/keyboard-open-detect"

const KEYBOARD_INSET_THRESHOLD_PX = 48
export const KEYBOARD_FIELD_GAP_PX = 12

/** Height reported by @capacitor/keyboard (fallback when visualViewport does not shrink). */
let pluginKeyboardHeightPx = 0

export function setPluginKeyboardHeight(px: number) {
  pluginKeyboardHeightPx = Math.max(0, px)
  syncAllKeyboardLayouts()
}

export function clearPluginKeyboardHeight() {
  pluginKeyboardHeightPx = 0
}

type ChatKeyboardMode = "layout-shrink" | "overlay"

function resolveOverlayKeyboardInsetPx(): number {
  const vvPx = measureViewportKeyboardOcclusionPx()

  if (vvPx >= KEYBOARD_INSET_THRESHOLD_PX) {
    return vvPx
  }

  return pluginKeyboardHeightPx
}

function resolveChatKeyboardMode(): ChatKeyboardMode {
  if (isLayoutViewportShrunkForKeyboard()) {
    return "layout-shrink"
  }
  return "overlay"
}

function resolveVisibleViewportHeight(): number {
  const vv = window.visualViewport
  const occlusion = measureViewportKeyboardOcclusionPx()

  if (vv && occlusion >= KEYBOARD_INSET_THRESHOLD_PX) {
    return Math.round(vv.offsetTop + vv.height)
  }

  if (pluginKeyboardHeightPx >= KEYBOARD_INSET_THRESHOLD_PX) {
    return Math.max(0, window.innerHeight - pluginKeyboardHeightPx)
  }

  if (vv) {
    return Math.round(vv.offsetTop + vv.height)
  }

  return window.innerHeight
}

/** Bottom edge of the visible area above the on-screen keyboard. */
export function getKeyboardVisibleBottom(): number {
  const visibleH = resolveVisibleViewportHeight()
  const vv = window.visualViewport
  const top = vv ? Math.round(vv.offsetTop) : 0
  return top + visibleH - KEYBOARD_FIELD_GAP_PX
}

/** Top edge of the visible area (below any browser chrome offset). */
export function getKeyboardVisibleTop(): number {
  const vv = window.visualViewport
  if (vv) {
    return Math.round(vv.offsetTop + KEYBOARD_FIELD_GAP_PX)
  }
  return KEYBOARD_FIELD_GAP_PX
}

/** Height of the visible viewport while the keyboard is open. */
export function getKeyboardVisibleHeight(): number {
  return resolveVisibleViewportHeight()
}

function clearGlobalKeyboardViewportResize() {
  if (typeof document === "undefined") return

  const html = document.documentElement
  html.removeAttribute("data-cap-keyboard-resize")
  html.style.removeProperty("--cap-keyboard-visible-h")
  html.style.removeProperty("--cap-keyboard-offset-top")

  for (const dialog of document.querySelectorAll<HTMLElement>('[data-app-modal="dialog"], [role="alertdialog"]')) {
    dialog.removeAttribute("data-capacitor-keyboard-expanded")
    dialog.style.removeProperty("padding-bottom")
  }
}

function shouldApplyKeyboardViewportResize() {
  return isNativeKeyboardOpen() || measureNativeKeyboardOpen()
}

/** Shrink scroll containers to the visible viewport while the keyboard is open. */
export function applyGlobalKeyboardViewportResize() {
  if (typeof document === "undefined" || !shouldApplyKeyboardViewportResize()) {
    clearGlobalKeyboardViewportResize()
    return
  }

  const visibleH = getKeyboardVisibleHeight()
  const offsetTop = window.visualViewport?.offsetTop ?? 0
  const html = document.documentElement

  html.setAttribute("data-cap-keyboard-resize", "")
  html.style.setProperty("--cap-keyboard-visible-h", `${visibleH}px`)
  html.style.setProperty("--cap-keyboard-offset-top", `${Math.round(offsetTop)}px`)
}

/** Reposition fixed chat shell above the keyboard (uses --cap-keyboard-inset on main.chat-mobile-fixed). */
export function syncKeyboardChromeLayout() {
  if (typeof document === "undefined") return

  const chatMain = document.querySelector<HTMLElement>("main.chat-mobile-fixed")
  if (!chatMain?.hasAttribute("data-capacitor-keyboard-expanded")) return

  const mode = resolveChatKeyboardMode()
  chatMain.setAttribute("data-cap-keyboard-mode", mode)

  if (mode === "layout-shrink") {
    chatMain.style.removeProperty("--cap-keyboard-inset")
  } else {
    chatMain.style.setProperty("--cap-keyboard-inset", `${resolveOverlayKeyboardInsetPx()}px`)
  }

  requestAnimationFrame(() => {
    const messages = chatMain.querySelector<HTMLElement>("[data-chat-messages-scroll]")
    if (messages) {
      messages.scrollTop = messages.scrollHeight
    }
  })
}

/** Hide bottom chrome while typing; resize modals/pages to fit above the keyboard. */
export function setKeyboardChromeExpanded(expanded: boolean) {
  if (typeof document === "undefined") return

  for (const el of document.querySelectorAll<HTMLElement>("[data-bottom-nav], [data-app-fab]")) {
    if (expanded) {
      el.setAttribute("data-capacitor-keyboard-hidden", "")
    } else {
      el.removeAttribute("data-capacitor-keyboard-hidden")
    }
  }

  const chatMain = document.querySelector<HTMLElement>("main.chat-mobile-fixed")
  if (chatMain) {
    if (expanded) {
      chatMain.setAttribute("data-capacitor-keyboard-expanded", "")
      syncKeyboardChromeLayout()
    } else {
      chatMain.removeAttribute("data-capacitor-keyboard-expanded")
      chatMain.removeAttribute("data-cap-keyboard-mode")
      chatMain.style.removeProperty("--cap-keyboard-inset")
    }
  }

  syncAllKeyboardLayouts()
}

export function isModalOverlayOpen() {
  return document.documentElement.hasAttribute("data-modal-overlay-open")
}

export function isFocusInModal(element: EventTarget | null) {
  return element instanceof HTMLElement && !!element.closest('[role="dialog"]')
}

/** @deprecated Use applyGlobalKeyboardViewportResize via syncAllKeyboardLayouts */
export function syncModalKeyboardLayout() {
  syncAllKeyboardLayouts()
}

/** Resize modals/pages for the keyboard and refresh chat chrome. */
export function syncAllKeyboardLayouts() {
  if (typeof document === "undefined") return

  if (shouldApplyKeyboardViewportResize()) {
    applyGlobalKeyboardViewportResize()
  } else {
    clearGlobalKeyboardViewportResize()
  }

  syncKeyboardChromeLayout()
}

export function resetKeyboardChrome() {
  clearPluginKeyboardHeight()
  clearGlobalKeyboardViewportResize()

  if (typeof document === "undefined") return

  for (const el of document.querySelectorAll<HTMLElement>("[data-bottom-nav], [data-app-fab]")) {
    el.removeAttribute("data-capacitor-keyboard-hidden")
  }

  const chatMain = document.querySelector<HTMLElement>("main.chat-mobile-fixed")
  if (chatMain) {
    chatMain.removeAttribute("data-capacitor-keyboard-expanded")
    chatMain.removeAttribute("data-cap-keyboard-mode")
    chatMain.style.removeProperty("--cap-keyboard-inset")
  }
}
